// Native mute means capture has stopped; the call's Mute button is separate.
export function watchMicrophoneAvailability(
  stream: MediaStream,
  onChange: (available: boolean) => void,
  devices: MediaDevices | undefined = navigator.mediaDevices,
): () => void {
  const tracks = stream.getAudioTracks();
  const inputsAtStart = tracks.map((track) => track.getSettings());
  let active = true;
  let available = true;
  let missing = false;
  let revision = 0;
  const update = () => {
    if (!active) return;
    const next =
      tracks.length > 0 &&
      !missing &&
      tracks.every((track) => track.readyState !== "ended" && !track.muted);
    if (next === available) return;
    available = next;
    onChange(next);
  };
  const checkDevices = async () => {
    const check = ++revision;
    update();
    if (!devices?.enumerateDevices) return;
    try {
      const inputs = await devices.enumerateDevices();
      if (!active || check !== revision) return;
      missing = inputsAtStart.some(({ deviceId, groupId }) => {
        // The "default" id can survive unplugging by switching to another device.
        return (
          !!deviceId &&
          !inputs.some(
            (device) =>
              device.kind === "audioinput" &&
              device.deviceId === deviceId &&
              (!groupId || device.groupId === groupId),
          )
        );
      });
      update();
    } catch {
      // Permission/enumeration failures do not establish hardware removal.
    }
  };
  for (const track of tracks) {
    track.addEventListener("ended", update);
    track.addEventListener("mute", update);
    track.addEventListener("unmute", checkDevices);
  }
  devices?.addEventListener?.("devicechange", checkDevices);
  update();
  return () => {
    active = false;
    for (const track of tracks) {
      track.removeEventListener("ended", update);
      track.removeEventListener("mute", update);
      track.removeEventListener("unmute", checkDevices);
    }
    devices?.removeEventListener?.("devicechange", checkDevices);
  };
}
